'use client';

/**
 * RfiAnalysisPanel — BFF-driven RFQ quote analysis
 *
 * Fetches pre-computed analysis via the rfi_analysis data gateway strategy.
 * Server computes vendor totals, price variance, and recommendation.
 *
 * @layer presentation
 * @arch_component_type panel
 * @arch_domain rfi
 */

import {
  Loader2,
  XCircle,
  BarChart3,
  CheckCircle,
  TrendingDown,
  TrendingUp,
  Package,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils/cn';
import { useStrategyData } from '@/lib/api/hooks';

interface RfiAnalysisPanelProps {
  entityId: string | null;
}

interface VendorTotal {
  vendor_name: string;
  vendor_sid: string;
  total: number;
  quoted_items: number;
  coverage: number;
  avg_lead_time: number | null;
}

interface PriceVariance {
  product_name: string;
  product_sid: string;
  spread: number;
  best: { vendor_name: string; price: number } | null;
  worst: { vendor_name: string; price: number } | null;
  missing_quotes: number;
}

interface AnalysisData {
  rfq_name: string | null;
  rfq_status: string | null;
  total_vendors: number;
  total_products: number;
  vendor_totals: VendorTotal[];
  high_variance_products: PriceVariance[];
  missing_quotes: PriceVariance[];
  best_vendor: VendorTotal | null;
}

export function RfiAnalysisPanel({ entityId }: RfiAnalysisPanelProps) {
  const { data, isLoading } = useStrategyData<AnalysisData>(
    'rfi_analysis',
    { rfq_sid: entityId ?? '' },
    !!entityId,
  );

  if (!entityId) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-center text-muted-foreground p-4">
        <BarChart3 className="h-8 w-8 mb-2 opacity-50" />
        <p className="text-sm">Select an RFQ to see analysis</p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-center text-muted-foreground">
        <Loader2 className="h-8 w-8 mb-2 animate-spin" />
        <p className="text-sm">Analyzing quotes...</p>
      </div>
    );
  }

  if (data && (!data.success || data.error)) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-center text-destructive">
        <XCircle className="h-8 w-8 mb-2" />
        <p className="text-sm">Failed to load analysis</p>
      </div>
    );
  }

  const analysis = data?.data;

  if (!analysis || analysis.total_vendors === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-center text-muted-foreground">
        <BarChart3 className="h-8 w-8 mb-2 opacity-50" />
        <p className="text-sm">No vendor responses yet</p>
        <p className="text-xs mt-1">Waiting for vendors to submit quotes</p>
      </div>
    );
  }

  const formatCurrency = (value: number) =>
    new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);

  return (
    <div className="flex flex-col h-full">
      {/* Sticky header */}
      <div className="sticky top-0 z-10 border-b border-border bg-background/95 backdrop-blur-sm px-3 py-2.5 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <BarChart3 className="h-4 w-4 text-primary" />
          <span className="text-sm font-semibold">Analysis</span>
        </div>
        <div className="flex items-center gap-1.5">
          <Badge variant="secondary" className="text-[10px] px-1.5">
            {analysis.total_vendors} vendors
          </Badge>
          <Badge variant="secondary" className="text-[10px] px-1.5">
            {analysis.total_products} items
          </Badge>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto scrollbar-thin">
        <div className="space-y-4 p-4">
          {/* RFQ Summary Card */}
          <div className="glass-card rounded-lg p-3">
            <div className="flex items-center justify-between">
              <div className="font-medium text-sm truncate flex-1">{analysis.rfq_name || 'RFQ'}</div>
              {analysis.rfq_status && (
                <span
                  className={cn(
                    'text-[10px] px-1.5 py-0.5 rounded-md',
                    analysis.rfq_status === 'sent' && 'bg-info/20 text-info',
                    analysis.rfq_status === 'done' && 'bg-success/20 text-success',
                    analysis.rfq_status === 'draft' && 'bg-muted text-muted-foreground',
                  )}
                >
                  {analysis.rfq_status}
                </span>
              )}
            </div>
          </div>

          {/* Recommendation */}
          {analysis.best_vendor && (
            <div>
              <div className="text-[10px] font-semibold text-muted-foreground mb-2 tracking-wider">
                RECOMMENDATION
              </div>
              <div className="rounded-lg bg-success/5 border border-success/20 p-3">
                <div className="flex items-center gap-2 mb-2">
                  <div className="p-1 rounded-md bg-success/10">
                    <CheckCircle className="h-3.5 w-3.5 text-success" />
                  </div>
                  <span className="font-semibold text-sm">{analysis.best_vendor.vendor_name}</span>
                </div>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div className="p-1.5 rounded bg-success/5">
                    <div className="text-[10px] text-muted-foreground">Total</div>
                    <div className="font-semibold text-success">{formatCurrency(analysis.best_vendor.total)}</div>
                  </div>
                  <div className="p-1.5 rounded bg-success/5">
                    <div className="text-[10px] text-muted-foreground">Coverage</div>
                    <div className="font-semibold">{analysis.best_vendor.coverage}%</div>
                  </div>
                  {analysis.best_vendor.avg_lead_time && (
                    <div className="col-span-2 p-1.5 rounded bg-success/5">
                      <div className="text-[10px] text-muted-foreground">Avg Lead Time</div>
                      <div className="font-semibold">{analysis.best_vendor.avg_lead_time} days</div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Vendor Totals */}
          {analysis.vendor_totals.length > 0 && (
            <div>
              <div className="text-[10px] font-semibold text-muted-foreground mb-2 tracking-wider">
                VENDOR TOTALS
              </div>
              <div className="space-y-1.5">
                {analysis.vendor_totals.map((vt, idx) => (
                  <div
                    key={vt.vendor_sid}
                    className={cn(
                      'flex items-center justify-between p-2.5 rounded-lg border border-l-4 transition-colors',
                      idx === 0
                        ? 'bg-success/5 border-success/20 border-l-success'
                        : 'bg-card/80 border-border/50 border-l-muted-foreground/30 hover:border-border',
                    )}
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      {idx === 0 && (
                        <div className="p-1 rounded bg-success/10">
                          <TrendingDown className="h-3 w-3 text-success" />
                        </div>
                      )}
                      <span className="text-sm truncate">{vt.vendor_name}</span>
                    </div>
                    <div className="text-right shrink-0 ml-2">
                      <div className={cn('text-sm font-semibold', idx === 0 && 'text-success')}>
                        {formatCurrency(vt.total)}
                      </div>
                      <div className="text-[10px] text-muted-foreground">
                        {vt.quoted_items}/{analysis.total_products} items
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* High Variance */}
          {analysis.high_variance_products.length > 0 && (
            <div>
              <div className="text-[10px] font-semibold text-muted-foreground mb-2 tracking-wider">
                PRICE VARIANCE
              </div>
              <div className="space-y-1.5">
                {analysis.high_variance_products.map((p) => (
                  <div key={p.product_sid} className="rounded-lg bg-warning/5 border border-warning/20 p-2.5">
                    <div className="flex items-start gap-2">
                      <div className="p-1 rounded bg-warning/10 shrink-0">
                        <TrendingUp className="h-3 w-3 text-warning" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium truncate">{p.product_name}</div>
                        <div className="text-[11px] text-muted-foreground mt-0.5">
                          {p.spread}% spread: {p.best?.vendor_name} ({formatCurrency(p.best?.price || 0)})
                          {' vs '}{p.worst?.vendor_name} ({formatCurrency(p.worst?.price || 0)})
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Missing Quotes */}
          {analysis.missing_quotes.length > 0 && (
            <div>
              <div className="text-[10px] font-semibold text-muted-foreground mb-2 tracking-wider">
                INCOMPLETE QUOTES
              </div>
              <div className="rounded-lg bg-muted/30 border border-border/50 p-2.5">
                <div className="flex items-center gap-2 text-sm">
                  <div className="p-1 rounded bg-muted">
                    <Package className="h-3 w-3 text-muted-foreground" />
                  </div>
                  <span>
                    {analysis.missing_quotes.length} product{analysis.missing_quotes.length !== 1 ? 's' : ''} missing coverage
                  </span>
                </div>
                <div className="mt-1.5 ml-7 text-xs text-muted-foreground">
                  {analysis.missing_quotes
                    .slice(0, 3)
                    .map((p) => p.product_name)
                    .join(', ')}
                  {analysis.missing_quotes.length > 3 &&
                    ` +${analysis.missing_quotes.length - 3} more`}
                </div>
              </div>
            </div>
          )}

          {/* Footer */}
          <div className="text-[10px] text-muted-foreground/60 text-center pt-1 pb-2">
            Based on {analysis.total_vendors} vendor response{analysis.total_vendors !== 1 ? 's' : ''}
          </div>
        </div>
      </div>
    </div>
  );
}
